import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { MERCHANT, REFRESH_TOKEN_KEY, StoreExample, StoreItemExample, TOKEN_KEY } from "../commons/constants";
import defaultAxios, { handleRefreshToken } from "../commons/utils/axios";
import { getMerchantLocal } from "../commons/utils/functions/getUserFromToken";
import {
  ALL_ATTRIBUTES_URL,
  CATEGORIES_URL,
  MERCHANT_STORES_URL,
  STORE_DETAIL_URL,
} from "../commons/constants/api-urls";
import useFetch from "../commons/hooks/useFetch";
import { MODES } from "../commons/constants/user";
import React from "react";
import { findStore } from "../commons/utils/functions/helper";
import { useNavigate } from "react-router-dom";
import { routers } from "../commons/constants/routers";
import { message } from "antd";
import { STORE_TYPE } from "../commons/constants/store";
import { STORE_TYPE_TABS } from "../components/StoreManagement";
import useFetchInfinity from "../commons/hooks/useFetchInfinity";
import { filterDuplicate } from "../commons/utils/functions/filterDuplicate";

interface AppState {
  user: Partial<User> | null;
  setUser: React.Dispatch<React.SetStateAction<Partial<User> | null>>;
  logout: () => void;
  allStores: FetchReturnType<Store>;
  foodDeliveryStores: FetchReturnType<Store>;
  surplusStores: FetchReturnType<Store>;
  currentStore: StoreItem;
  setCurrentStore: React.Dispatch<React.SetStateAction<StoreItem>>;
  categories: Category[];
  currentStoreDetail: Store;
  currentStoreDetailCategory: Category[];
  configAttributes: Attribute[];
  findAndSetStore: (storeId: string) => void;
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
  clearUserData: () => void;
  refetchStoreData: (type: STORE_TYPE_TABS) => void;
  getTotalStoreData: (type: STORE_TYPE_TABS) => number;
  getAllStoreData: () => Store[];
  getStoresData: (type: STORE_TYPE_TABS) => Store[];
  nextStores: (type: STORE_TYPE_TABS) => void;
  refreshAll: () => void;
  checkHasMoreStore: (type: STORE_TYPE_TABS) => boolean;
  handleFindStore: (id: string) => string | null;
  getAllStoresForSearch: () => void;
  loading: boolean;
}

interface FetchReturnType<Store> {
  data: Store[];
  loading: boolean;
  refresh: () => void;
  next: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  hasMore: boolean;
  total: number | null;
}

const initialList: FetchReturnType<Store> = {
  data: [],
  loading: false,
  total: 0,
  refresh: () => null,
  setLoading: () => null,
  next: () => null,
  hasMore: false,
};

export const AppContext = createContext<AppState>({
  user: {},
  setUser: () => {},
  logout: () => {},
  allStores: { ...initialList },
  foodDeliveryStores: { ...initialList },
  surplusStores: { ...initialList },
  currentStore: StoreItemExample,
  setCurrentStore: () => {},
  categories: [],
  configAttributes: [],
  findAndSetStore: () => {},
  currentStoreDetail: StoreExample,
  currentStoreDetailCategory: [],
  isMobile: false,
  setIsMobile: () => {},
  clearUserData: () => {},
  refetchStoreData: () => {},
  getTotalStoreData: () => 0,
  getAllStoreData: () => [],
  getStoresData: () => [],
  nextStores: () => {},
  refreshAll: () => {},
  getAllStoresForSearch: () => {},
  checkHasMoreStore: () => false,
  handleFindStore: (id: string) => null,
  loading: false,
});

export const AppProvider: React.FC<{ children: React.ReactNode }> = props => {
  const navigate = useNavigate();
  const [user, setUser] = useState<Partial<User> | null>({});

  const [isMobile, setIsMobile] = useState(false);
  const [currentStore, setCurrentStore] = useState<StoreItem>(StoreItemExample);
  const [currentStoreDetail, setCurrentStoreDetail] = useState<Store>(StoreExample);
  const [perPage, setPerPage] = useState(12);

  const allStores = useFetchInfinity<Store>(
    user?.merchantIds && Number(user?.merchant_role?.id) === MODES.ADMIN
      ? MERCHANT_STORES_URL(user.merchantIds[0])
      : "",
    {
      pageSize: perPage,
      perPage: perPage,
    }
  );

  const foodDeliveryStores = useFetchInfinity<Store>(
    user?.merchantIds && Number(user?.merchant_role?.id) === MODES.ADMIN
      ? MERCHANT_STORES_URL(user.merchantIds[0])
      : "",
    {
      type: STORE_TYPE.FOOD_DELIVERY,
      pageSize: 12,
      perPage: 12,
    }
  );

  const surplusStores = useFetchInfinity<Store>(
    user?.merchantIds && Number(user?.merchant_role?.id) === MODES.ADMIN
      ? MERCHANT_STORES_URL(user.merchantIds[0])
      : "",
    {
      type: STORE_TYPE.SURPLUS,
      pageSize: 12,
      perPage: 12,
    }
  );

  const getAllStoresForSearch = () => {
    setPerPage(1000);
  };

  const getAllStoreData = () => {
    return filterDuplicate([...allStores.data, ...foodDeliveryStores.data, ...surplusStores.data]);
  };

  const refreshAll = () => {
    setPerPage(12);
    refetchStoreData(STORE_TYPE_TABS.ALL);
    refetchStoreData(STORE_TYPE_TABS.FOOD_DELIVERY);
    refetchStoreData(STORE_TYPE_TABS.SURPLUS);
  };

  const { data: categories } = useFetch<Category[]>(CATEGORIES_URL);
  const { data: configAttributes } = useFetch<Attribute[]>(ALL_ATTRIBUTES_URL);

  const refetchStoreData = (type: STORE_TYPE_TABS) => {
    switch (type) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        foodDeliveryStores.refresh();
        break;
      case STORE_TYPE_TABS.SURPLUS:
        surplusStores.refresh();
        break;
      default:
        allStores.refresh();
        break;
    }
  };

  const getStoresData = (type: STORE_TYPE_TABS) => {
    switch (type) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        return foodDeliveryStores.data;
      case STORE_TYPE_TABS.SURPLUS:
        return surplusStores.data;
      default:
        return allStores.data;
    }
  };

  const getTotalStoreData = (type: STORE_TYPE_TABS) => {
    switch (type) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        return foodDeliveryStores.total || 0;
      case STORE_TYPE_TABS.SURPLUS:
        return surplusStores.total || 0;
      default:
        return allStores.total || 0;
    }
  };

  const checkHasMoreStore = (type: STORE_TYPE_TABS) => {
    switch (type) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        return foodDeliveryStores.hasMore || false;
      case STORE_TYPE_TABS.SURPLUS:
        return surplusStores.hasMore || false;
      default:
        return allStores.hasMore || false;
    }
  };

  const nextStores = (type: STORE_TYPE_TABS) => {
    switch (type) {
      case STORE_TYPE_TABS.FOOD_DELIVERY:
        foodDeliveryStores.next();
        break;
      case STORE_TYPE_TABS.SURPLUS:
        surplusStores.next();
        break;
      default:
        allStores.next();
        break;
    }
  };

  async function findAndSetStore(storeId: string) {
    const store = getAllStoreData();
    const chosenStore = findStore(store, storeId);

    if (chosenStore) {
      setCurrentStore(chosenStore);
    } else {
      const data = await getCurrentStoreDetail(storeId);
      if (data) {
        setCurrentStore(data);
        return;
      }
      message.error("Store not found");
      setTimeout(() => {
        navigate(routers.STORES.LIST);
      }, 1000);
    }
  }

  const handleFindStore = (id: string) => {
    if (!id) return null;
    const store = getAllStoreData();
    return findStore(store, id)?.name || "";
  };

  function clearUserData() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(MERCHANT);
    setUser(null);
    setCurrentStore(StoreItemExample);
    setCurrentStoreDetail(StoreExample);
  }

  async function getCurrentStoreDetail(storeId: string) {
    try {
      const res = await defaultAxios.get(STORE_DETAIL_URL(storeId));
      return res.data;
    } catch (error) {
      message.error("Store not found");
    }
  }

  const getUserFromRefreshToken = async () => {
    const refresh_token = await handleRefreshToken();
    if (refresh_token) {
      const user = getMerchantLocal();

      if (!user) {
        logout();
        return;
      }

      if (user.merchant_role.id === MODES.USER) {
        setUser(user);
        // setCurrent when merchant member login
        setCurrentStore(user.merchantStoreAuthorities[0].merchantStore);
      } else {
        setUser(user);
      }
    } else {
      logout();
    }
  };

  useEffect(() => {
    getUserFromRefreshToken();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (currentStore.id && currentStore.id !== StoreExample.id) {
        const data = await getCurrentStoreDetail(currentStore.id);
        setCurrentStoreDetail(data);
      }
    };

    fetchData();
  }, [currentStore]);

  const getCurrentStoreDetailCategory = () => {
    const storeCate = [
      ...currentStoreDetail.categoriesLevel1,
      ...currentStoreDetail.categoriesLevel2,
      ...currentStoreDetail.categoriesLevel3,
    ];
    return storeCate;
  };

  const currentStoreDetailCategory = useMemo(() => {
    if (!currentStoreDetail?.id && currentStoreDetail?.id !== StoreExample.id) return [];
    return getCurrentStoreDetailCategory();
  }, [currentStoreDetail]);

  const logout = useCallback(() => {
    clearUserData();
  }, []);

  const appState = {
    user,
    setUser,
    logout,
    allStores,
    foodDeliveryStores,
    surplusStores,
    currentStore,
    currentStoreDetail,
    currentStoreDetailCategory,
    setCurrentStore,
    categories: categories || [],
    configAttributes: configAttributes || [],
    findAndSetStore,
    isMobile,
    setIsMobile,
    clearUserData,
    getTotalStoreData,
    refetchStoreData,
    getAllStoreData,
    getStoresData,
    nextStores,
    refreshAll,
    checkHasMoreStore,
    getAllStoresForSearch,
    handleFindStore,
    loading: allStores.loading || foodDeliveryStores.loading || surplusStores.loading,
  };
  return <AppContext.Provider value={appState}>{props.children}</AppContext.Provider>;
};
